/**
 * Authentication API Service
 * Communicates with Slope server auth endpoints
 */

import type { User } from '../contexts/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

interface LoginResponse {
  authorizationUrl: string;
  state: string;
}

interface AuthStatusResponse {
  authenticated: boolean;
  user?: {
    id: string;
    email: string;
    displayName: string;
    departments: string[];
    primaryDepartment: string;
    roles: string[];
    primaryRole: string;
  };
  session?: {
    expiresAt: Date;
    lastActivity: Date;
  };
}

interface RefreshResponse {
  success: boolean;
  refreshed: boolean;
  expiresAt: Date;
  expiresIn: number;
  sessionExpiresAt?: Date;
  sessionExpiresIn?: number;
  error?: string;
  code?: string;
}

class AuthService {
  /**
   * Get session ID from localStorage
   */
  getSessionId(): string | null {
    return localStorage.getItem('sessionId');
  }

  /**
   * Set session ID in localStorage
   */
  setSessionId(sessionId: string): void {
    localStorage.setItem('sessionId', sessionId);
  }

  /**
   * Clear session ID from localStorage
   */
  clearSessionId(): void {
    localStorage.removeItem('sessionId');
  }

  /**
   * Initiate Azure SSO login
   */
  async initiateLogin(redirectUrl?: string): Promise<LoginResponse> {
    const params = new URLSearchParams();
    if (redirectUrl) {
      params.append('redirectUrl', redirectUrl);
    }

    const response = await fetch(
      `${API_BASE_URL}/auth/v1/azure/login?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to initiate login');
    }

    return response.json();
  }

  /**
   * Exchange the one-time login code from the callback URL for a session.
   *
   * The session ID arrives in this response body and nowhere else. The code
   * is single-use and lives for a minute, so a stale or replayed callback page
   * gets a 401 rather than a session.
   */
  async exchangeLoginCode(code: string): Promise<{ sessionId: string; expiresAt: Date }> {
    const response = await fetch(`${API_BASE_URL}/auth/v1/exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      throw new Error(
        response.status === 401
          ? 'This sign-in attempt has expired. Please try again.'
          : 'Authentication failed'
      );
    }

    const data = await response.json();
    return { sessionId: data.sessionId, expiresAt: new Date(data.expiresAt) };
  }

  /**
   * Logout
   */
  async logout(options?: { logoutFromAzure?: boolean; allSessions?: boolean }): Promise<void> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return;
    }

    const response = await fetch(`${API_BASE_URL}/auth/v1/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Id': sessionId,
      },
      body: JSON.stringify({
        logoutFromAzure: options?.logoutFromAzure,
        allSessions: options?.allSessions,
      }),
    });

    if (!response.ok) {
      console.error('Logout failed');
    }

    // Clear session ID regardless
    this.clearSessionId();

    // If logging out from Azure, redirect to Azure logout URL
    if (options?.logoutFromAzure) {
      const data = await response.json();
      if (data.azureLogoutUrl) {
        window.location.href = data.azureLogoutUrl;
      }
    }
  }

  /**
   * Check authentication status
   */
  async checkStatus(): Promise<AuthStatusResponse> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return { authenticated: false };
    }

    const response = await fetch(`${API_BASE_URL}/auth/v1/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Id': sessionId,
      },
    });

    if (!response.ok) {
      return { authenticated: false };
    }

    return response.json();
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<User> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/auth/v1/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Id': sessionId,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get profile');
    }

    const data = await response.json();
    return data.user;
  }

  /**
   * Refresh session tokens
   * Returns true if session is still valid, false if re-login needed
   */
  async refreshTokens(): Promise<RefreshResponse> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return {
        success: false,
        refreshed: false,
        expiresAt: new Date(),
        expiresIn: 0,
        error: 'No session',
        code: 'NO_SESSION',
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/v1/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId,
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          refreshed: false,
          expiresAt: new Date(),
          expiresIn: 0,
          error: data.error || 'Refresh failed',
          code: data.code || 'REFRESH_FAILED',
        };
      }

      return {
        success: true,
        refreshed: data.refreshed,
        expiresAt: new Date(data.expiresAt),
        expiresIn: data.expiresIn,
        sessionExpiresAt: data.sessionExpiresAt ? new Date(data.sessionExpiresAt) : undefined,
        sessionExpiresIn: data.sessionExpiresIn,
      };
    } catch (error) {
      console.error('Token refresh error:', error);
      return {
        success: false,
        refreshed: false,
        expiresAt: new Date(),
        expiresIn: 0,
        error: 'Network error',
        code: 'NETWORK_ERROR',
      };
    }
  }

  /**
   * Force refresh tokens AND extend the session duration
   * Used when user confirms they are still active
   */
  async forceRefreshAndExtend(): Promise<RefreshResponse> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return {
        success: false,
        refreshed: false,
        expiresAt: new Date(),
        expiresIn: 0,
        error: 'No session',
        code: 'NO_SESSION',
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/v1/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId,
        },
        body: JSON.stringify({ force: true, extend: true }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          refreshed: false,
          expiresAt: new Date(),
          expiresIn: 0,
          error: data.error || 'Refresh failed',
          code: data.code || 'REFRESH_FAILED',
        };
      }

      return {
        success: true,
        refreshed: data.refreshed,
        expiresAt: new Date(data.expiresAt),
        expiresIn: data.expiresIn,
        sessionExpiresAt: data.sessionExpiresAt ? new Date(data.sessionExpiresAt) : undefined,
        sessionExpiresIn: data.sessionExpiresIn,
      };
    } catch (error) {
      console.error('Force refresh error:', error);
      return {
        success: false,
        refreshed: false,
        expiresAt: new Date(),
        expiresIn: 0,
        error: 'Network error',
        code: 'NETWORK_ERROR',
      };
    }
  }
}

export const authService = new AuthService();
